import { useEffect, useMemo, useState } from 'react';

import { PaginationState } from '@tanstack/react-table';
import { useSearchParams, useParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';

import { FactsTable } from './components/facts-table';
import { FactsTableHeader } from './components/facts-table-header';
import { FactsTableLoader } from './components/facts-table-loader';
import { IncludeAllDropdown } from './components/include-all';
import { PageName, TrackingEvents } from '@/analytics/events';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import useToggleAll from '@/api/mutations/useToggleAllIncluded';
import { useGetCase } from '@/api/queries/useGetCase';
import useFactsData from '@/api/queries/useGetFacts';
import { useFetchIncludedFactsCount } from '@/api/queries/useGetIncludedFactsCount';
import { useGetKeyFacts } from '@/api/queries/useGetKeyFacts';
import { useGetPersons } from '@/api/queries/useGetPersons';
import { useFactNaturalLanguageFilters } from '@/hooks/useNaturalLanguageFilters';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { FactDocViewer } from '@/new/components/doc-viewer/fact-doc-viewer';
import { FilterPills } from '@/new/components/filter-pills';
import { FactFilters } from '@/types';
import { buildFactFilterPills } from '@/utils/buildFilterPills';
import { createPersonsMap } from '@/utils/filterLookups';
import { removeFilterValue } from '@/utils/filterUtils';

export function Facts() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) {
    return null;
  }

  const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
  const [docId, setDocId] = useState<string | null>(null);
  const [factId, setFactId] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });

  const [searchParams, setSearchParams] = useSearchParams();

  // Initialise filters with sessionStorage persistence
  const defaultFilters: FactFilters = {
    docIds: [],
    excludedDocIds: [],
    relevance: [],
    classification: [],
    keyFacts: false,
    disputedFacts: false,
    startDate: null,
    endDate: null,
    runIds: [],
    personIds: [],
    documentTypes: [],
    themes: [],
    searchQuery: '',
  };
  const [filters, setFilters] = useSessionStorage<FactFilters>(`case-${matterId}-facts-table-filters`, defaultFilters);
  const [searchQueryInput, setSearchQueryInput] = useState(filters.searchQuery ?? '');

  const { mutateAsync: toggleAllIncluded } = useToggleAll(matterId);

  const { buildFiltersWithNL, isProcessing: isProcessingNLFiltersQuery } = useFactNaturalLanguageFilters({
    caseId: matterId,
    onFiltersChange: (newFilters: FactFilters) => {
      setFilters(newFilters);
      setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
    },
    onSearchChange: (searchQuery: string) => {
      setFilters((prev) => ({ ...prev, searchQuery: searchQuery }));
    },
  });

  const { data: allPersons } = useGetPersons({ caseId: matterId, paginated: false });
  const {
    data: responseFacts,
    isLoading,
    refetch: refetchCaseFacts,
  } = useFactsData({
    docId: null,
    caseId: matterId,
    searchQuery: filters.searchQuery,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filters,
  });
  const { data: includedFactsCountResponse } = useFetchIncludedFactsCount(matterId);
  const { data: keyFactsData } = useGetKeyFacts(matterId);
  const { data: caseData } = useGetCase(matterId);

  // Handlers
  const handleToggleAll = async ({
    checked: toggleValue,
    inViewOnly,
  }: {
    checked: boolean;
    inViewOnly: boolean;
  }): Promise<void> => {
    const factIdsInView = responseFacts?.facts.map((fact) => fact.id) ?? [];

    trackCustomEvent(toggleValue ? TrackingEvents.ToggleAllFactsOn : TrackingEvents.ToggleAllFactsOff, { matterId });
    await toggleAllIncluded({
      caseId: matterId,
      toggleValue,
      filters,
      factIds: inViewOnly ? factIdsInView : (responseFacts?.factIds ?? []),
    });
  };

  const onSearchCall = async (searchValue?: string, useNaturalLanguage?: boolean) => {
    trackCustomEvent(TrackingEvents.SearchFacts, { matterId });

    const value = searchValue ?? searchQueryInput;

    if (useNaturalLanguage && value.trim()) {
      buildFiltersWithNL(value);
      return;
    }

    // Regular search
    // Reset page to 0
    const newPaginationState = { pageIndex: 0, pageSize: pagination.pageSize };
    setPagination(newPaginationState);

    // Close the doc viewer if it is open
    setFilters((prev) => ({ ...prev, searchQuery: value }));
    setSearchQueryInput(value);
  };

  const clearSearch = async () => {
    setSearchQueryInput('');
    setFilters((prev) => ({ ...prev, searchQuery: '' }));
    await new Promise((resolve) => setTimeout(resolve, 0));
  };

  const handleRemoveFilter = (filterKey: string, value?: any) => {
    if (filterKey === 'searchQuery') {
      setSearchQueryInput('');
    }
    if (filterKey === 'personIds') {
      searchParams.delete('personId', value);
      setSearchParams(searchParams, { replace: true });
    }
    if (filterKey === 'themes') {
      searchParams.delete('themes', value);
      setSearchParams(searchParams, { replace: true });
    }

    setFilters((prev) => removeFilterValue(prev, filterKey as keyof FactFilters, value));

    // Reset pagination when filters change
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  const openDocViewer = (docId: string, factId: string) => {
    setIsDocViewerOpen(true);
    setDocId(docId);
    setFactId(factId);
  };

  // Pagination
  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: pagination.pageSize };
    setPagination(newPaginationState);
  };
  const prevPage = () => goToPage(pagination.pageIndex - 1);
  const nextPage = () => goToPage(pagination.pageIndex + 1);

  const handlePageSizeChange = (newPageSize: number) => {
    const newPaginationState = { pageIndex: 0, pageSize: newPageSize };
    setPagination(newPaginationState);
  };

  const handleClearAllFilters = () => {
    setFilters(defaultFilters);
    setSearchQueryInput('');

    // Clear URL params for key facts and disputed facts
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('viewKeyFacts');
    newParams.delete('viewDisputedFacts');
    newParams.delete('themes');
    // Remove all personId params
    const allKeys = Array.from(newParams.keys());
    allKeys.forEach((key) => {
      if (key === 'personId') {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams, { replace: true });

    // Reset pagination to first page
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  // useEffects for query param filters
  useEffect(() => {
    const personIds = searchParams.getAll('personId');
    if (personIds) {
      setFilters((prevFilters) => ({ ...prevFilters, personIds }));
    }
  }, [searchParams]);

  useEffect(() => {
    const themes = searchParams.getAll('themes');
    if (themes) {
      setFilters((prevFilters) => ({ ...prevFilters, themes }));
    }
  }, [searchParams]);

  const allRowsCheckedInView = useMemo(
    () => responseFacts?.facts.every((fact) => fact.included) ?? false,
    [responseFacts?.facts],
  );
  const anyRowsCheckedInView = useMemo(
    () => responseFacts?.facts.some((fact) => fact.included) ?? false,
    [responseFacts?.facts],
  );

  useEffect(() => {
    trackPageView(PageName.Facts);
  }, []);

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb
        items={
          caseData
            ? [
                { href: '/matters', label: 'Matters', active: false },
                { href: `/matters/${matterId}/subsets`, label: caseData.name, active: false },
                { href: `/matters/${matterId}/subsets/${subsetId}`, label: 'Subset Name', active: false },
                { href: `/matters/${matterId}/subsets/${subsetId}/facts`, label: 'Facts', active: true },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="px-4 pt-4">
          <FactsTableHeader
            // Search
            globalFilter={searchQueryInput}
            clearSearch={clearSearch}
            setGlobalFilter={setSearchQueryInput}
            onSearchCall={onSearchCall}
            refetchCaseFacts={refetchCaseFacts}
            resultsIncludedLength={includedFactsCountResponse ?? 0}
            // Pagination
            canGetPrevPage={pagination.pageIndex > 0}
            canGetNextPage={pagination.pageIndex < (responseFacts?.pageCount ?? 0) - 1}
            prevPage={prevPage}
            nextPage={nextPage}
            goToPage={goToPage}
            currentPage={pagination.pageIndex}
            noOfPages={responseFacts?.pageCount}
            pageSize={pagination.pageSize}
            onPageSizeChange={handlePageSizeChange}
            activeFilters={filters}
            setFilters={setFilters}
            caseRuns={caseData?.runs ?? []}
            allPersons={allPersons ?? []}
            onClusterComplete={() => {
              refetchCaseFacts();
            }}
            processingNL={isProcessingNLFiltersQuery}
            filterCount={
              Object.values(filters).filter(
                (value) =>
                  value !== undefined &&
                  value !== null &&
                  value !== '' &&
                  !(Array.isArray(value) && value.length === 0),
              ).length
            }
            totalUnfilteredCount={responseFacts?.totalUnfilteredCount}
            totalFilteredCount={responseFacts?.totalCount}
          />
          <FilterPills
            pills={buildFactFilterPills(filters, handleRemoveFilter, createPersonsMap(allPersons))}
            onClearAll={handleClearAllFilters}
          />
          <div className="flex items-center gap-2 py-2">
            <IncludeAllDropdown
              allRowsChecked={includedFactsCountResponse === responseFacts?.totalCount}
              anyRowsChecked={(includedFactsCountResponse ?? 0) > 0}
              allRowsCheckedInView={allRowsCheckedInView}
              anyRowsCheckedInView={anyRowsCheckedInView}
              handleSelectAll={({ inViewOnly }) => handleToggleAll({ checked: true, inViewOnly })}
              handleClearAll={({ inViewOnly }) => handleToggleAll({ checked: false, inViewOnly })}
            />
            {includedFactsCountResponse !== undefined && (
              <div className="text-xs text-gray-600">
                <b className="text-brandSecondary">{includedFactsCountResponse.toLocaleString()}</b> facts selected
              </div>
            )}
          </div>
        </div>

        <div className="mb-2 flex w-full flex-1 gap-1 overflow-hidden px-4">
          {isLoading ? (
            <FactsTableLoader />
          ) : (
            <div
              className={`h-full overflow-y-scroll py-0.5 transition-all duration-300 ${isDocViewerOpen ? 'w-7/12' : 'w-full'}`}
            >
              <FactsTable
                selectedFactId={factId ?? ''}
                facts={responseFacts?.facts || []}
                keyFactIdSet={new Set(keyFactsData?.map((fact) => fact.id) ?? [])}
                openDocViewer={openDocViewer}
              />
            </div>
          )}
          <div
            className={`transition-all duration-300 ease-in-out ${
              isDocViewerOpen ? 'w-5/12 opacity-100' : 'w-0 opacity-0'
            } overflow-hidden rounded-md border border-gray-300 bg-white`}
          >
            <div
              className={`h-full w-full transition-transform duration-300 ease-in-out ${
                isDocViewerOpen ? 'translate-x-0' : 'translate-x-full'
              }`}
            >
              {isDocViewerOpen && docId && matterId && factId && (
                <FactDocViewer
                  docId={docId}
                  caseId={matterId}
                  factId={factId}
                  onClose={() => {
                    setDocId(null);
                    setFactId(null);
                    setIsDocViewerOpen(false);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
